import { Divider, Tab, Tabs, Theme, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import * as React from "react";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import {
  Datagrid,
  Identifier,
  ListContextProvider,
  ListProps,
  TextField,
  useGetList,
  useListContext,
} from "react-admin";
import { CustomDateField } from "../../../Components/CustomDateField";
import { DetailButton } from "../../../Components/DetailButton";
import CreatedByField from "../../../Components/ReferenceFields/CreatedBy/CreatedByField";
import { DefaultCreatedDateSort, DefaultIsActiveFilter, DefaultPerPageSize } from "../../../Constants/Constant";
import { ActivePassiveStatus } from "../../../Enums/Active";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { TabbedDatagridProps } from "../../../Models/TabbedDataGridProps";
import { ReleaseTypeExcelHeader, ReleaseTypeExcelName } from "./Constant/Excel";
import { SourceReleaseType, TitleReleaseType } from "./Constant/Source";
import PoolList from "../../../Components/PoolListComponent/PoolListComponent";

const ExportToExcel = data => {
  ExcelExport(
    ReleaseTypeExcelHeader,
    data.map(v => [v.releaseTypeCode, v.releaseTypeName, v.createdBy, v.createdDate]),
    ReleaseTypeExcelName
  );
};

const useDatagridStyles = makeStyles({
  total: { fontWeight: "bold" },
});

const useGetTotals = (filterValues: any) => {
  const { total: totalIsActive } = useGetList(
    SourceReleaseType,
    { perPage: 100, page: 1 },
    { field: "createdDate", order: "DESC" },
    { ...filterValues, isActive__eq: true }
  );
  const { total: totalIsPassive } = useGetList(
    SourceReleaseType,
    { perPage: 100, page: 1 },
    { field: "createdDate", order: "DESC" },
    { ...filterValues, isActive__eq: false }
  );

  return {
    active: totalIsActive,
    passive: totalIsPassive,
  };
};

const TabbedDatagrid: FC<TabbedDatagridProps> = props => {
  const listContext = useListContext();
  const { ids, filterValues, setFilters, displayedFilters } = listContext;
  const isXSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down("xs"));
  const [isActive, setIsActive] = useState<Identifier[]>([] as Identifier[]);
  const [isPassive, setIsPassive] = useState<Identifier[]>([] as Identifier[]);
  const classes = useDatagridStyles();
  const totals = useGetTotals(filterValues) as any;

  useEffect(() => {
    if (ids && ids !== filterValues.isActive__eq) {
      switch (filterValues.isActive__eq) {
        case true:
          setIsActive(ids);
          break;
        case false:
          setIsPassive(ids);
          break;
      }
    }
  }, [ids, filterValues.isActive__eq]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<{}>, value: any) => {
      setFilters && setFilters({ ...filterValues, isActive__eq: value == 1 }, displayedFilters);
    },
    [displayedFilters, filterValues, setFilters]
  );

  const selectedIds = filterValues.isActive__eq == true ? isActive : filterValues.isActive__eq == false;

  return (
    <Fragment>
      <Tabs
        variant="fullWidth"
        centered
        value={filterValues.isActive__eq ? 1 : 0}
        indicatorColor="primary"
        onChange={handleChange}
      >
        {ActivePassiveStatus.map(choice => (
          <Tab
            key={choice.id}
            label={totals[choice.name] ? `${choice.name} (${totals[choice.name]})` : choice.name}
            value={choice.id}
          />
        ))}
      </Tabs>
      <Divider />
      <ListContextProvider
        value={{
          ...listContext,
          ids: filterValues.isActive_eq ? isActive : isPassive,
        }}
      >
        <Datagrid {...props} optimized hasBulkActions={false}>
          <DetailButton label="Actions" resource={SourceReleaseType} />
          <TextField source="releaseTypeCode" label="RELEASE TYPE CODE" />
          <TextField source="releaseTypeName" label="RELEASE TYPE NAME" />
          <CreatedByField label="CREATED BY" link={false} />
          <CustomDateField source="createdDate" label="CREATED DATE" showTime />
        </Datagrid>
      </ListContextProvider>
    </Fragment>
  );
};

const ReleaseTypeList: FC<ListProps> = props => (
  <PoolList
    {...props}
    title={TitleReleaseType.list}
    filterDefaultValues={DefaultIsActiveFilter}
    sort={DefaultCreatedDateSort}
    perPage={DefaultPerPageSize}
    exporter={ExportToExcel}
  >
    <TabbedDatagrid />
  </PoolList>
);

export default ReleaseTypeList;
